import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, Phone, Loader2, Building2, Handshake, ArrowRight, Trophy, Users, Megaphone, Home } from 'lucide-react';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AnimatedBackground from '../components/AnimatedBackground';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'participant';
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    role: ['participant', 'organizer', 'provider', 'sponsor'].includes(defaultRole) ? defaultRole : 'participant',
    company_name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error(t('auth.fillRequired')); return;
    }
    if (formData.role === 'provider' && !formData.company_name) {
      toast.error(t('auth.companyRequired')); return;
    }
    if (formData.role === 'sponsor' && !formData.company_name) {
      toast.error(t('auth.companyRequired')); return;
    }
    if (formData.password.length < 6) {
      toast.error(t('auth.passwordMin')); return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordMismatch')); return;
    }
    setLoading(true);
    try {
      const result = await register(formData.name, formData.email, formData.password, formData.phone, formData.role, formData.company_name);
      if (result?.pending) {
        toast.success(t('auth.registerPending'));
        navigate('/login');
      } else {
        toast.success(t('auth.registerSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registerError'));
    } finally { setLoading(false); }
  };

  const inputClass = "w-full h-11 pl-11 pr-4 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 focus:bg-white/[0.08] transition-all";

  const roles = [
    { id: 'participant', label: t('auth.roleParticipant'), desc: t('auth.roleParticipantDesc'), icon: Trophy },
    { id: 'organizer', label: t('auth.roleOrganizer'), desc: t('auth.roleOrganizerDesc'), icon: Users },
    { id: 'provider', label: t('auth.roleProvider'), desc: t('auth.roleProviderDesc'), icon: Handshake },
    { id: 'sponsor', label: t('auth.roleSponsor'), desc: t('auth.roleSponsorDesc'), icon: Megaphone }
  ];

  return (
    <AnimatedBackground>
      {/* Mini nav */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 py-3 flex items-center justify-between">
        <Link to="/">
          <img src="/logo-light.png" alt="iBookSport" className="h-8" />
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-heading font-bold uppercase tracking-wider transition-colors" data-testid="register-home-link">
          <Home className="w-4 h-4" /> Accueil
        </Link>
      </div>
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="register-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8">
            <Link to="/">
              <img src="/logo-light.png" alt="iBookSport" className="h-12 mx-auto" data-testid="register-logo" />
            </Link>
          </motion.div>

          {/* Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] overflow-hidden">

            {/* Header bar */}
            <div className="bg-brand px-8 py-5">
              <h1 className="font-heading text-xl font-black uppercase tracking-wider text-white">{t('auth.registerTitle')}</h1>
              <p className="text-white/70 text-sm mt-1">{t('auth.registerSubtitle')}</p>
            </div>

            {/* Role selector */}
            <div className="px-8 pt-6 pb-2">
              <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-3 block">{t('auth.role')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="role-selector">
                {roles.map(r => (
                  <button key={r.id} type="button" onClick={() => setFormData(p => ({ ...p, role: r.id }))}
                    className={`p-3 border text-center transition-all ${
                      formData.role === r.id
                        ? 'border-brand bg-brand/15 shadow-[0_0_12px_rgba(249,115,22,0.15)]'
                        : 'border-white/[0.08] hover:border-white/20 bg-white/[0.02]'
                    }`} data-testid={`role-${r.id}`}>
                    <r.icon className={`w-5 h-5 mx-auto mb-1.5 ${formData.role === r.id ? 'text-brand' : 'text-slate-500'}`} />
                    <p className={`text-[10px] font-heading font-bold uppercase tracking-wider ${formData.role === r.id ? 'text-brand' : 'text-slate-500'}`}>{r.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 pb-6 pt-4 space-y-4">
              <div>
                <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">
                  {formData.role === 'provider' ? t('auth.managerName') + ' *' : t('auth.name') + ' *'}
                </Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                  <input type="text" placeholder="Jean Dupont" value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    className={inputClass} data-testid="name-input" />
                </div>
              </div>

              {(formData.role === 'provider' || formData.role === 'sponsor') && (
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{formData.role === 'sponsor' ? t('auth.brandName') : t('auth.companyName')} *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="text" placeholder={formData.role === 'sponsor' ? 'Nike, Decathlon...' : 'SportWear Lyon'} value={formData.company_name}
                      onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
                      className={inputClass} data-testid="company-input" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.email')} *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="email" placeholder="votre@email.com" value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      className={inputClass} data-testid="email-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="tel" placeholder="+33 6 XX XX XX" value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      className={inputClass} data-testid="phone-input" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.password')} *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type={showPassword ? 'text' : 'password'} placeholder={t('auth.minChars')} value={formData.password}
                      onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                      className="w-full h-11 pl-11 pr-11 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 focus:bg-white/[0.08] transition-all"
                      data-testid="password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">{t('auth.confirmPassword')} *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type={showPassword ? 'text' : 'password'} placeholder={t('auth.confirmPlaceholder')} value={formData.confirmPassword}
                      onChange={(e) => setFormData(p => ({ ...p, confirmPassword: e.target.value }))}
                      className={inputClass} data-testid="confirm-password-input" />
                  </div>
                </div>
              </div>

              {formData.role === 'provider' && (
                <div className="text-[11px] text-amber-400/80 bg-amber-400/10 border border-amber-400/20 p-3 flex items-start gap-2">
                  <Handshake className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t('auth.providerWarning')}</span>
                </div>
              )}

              {formData.role === 'sponsor' && (
                <div className="text-[11px] text-amber-400/80 bg-amber-400/10 border border-amber-400/20 p-3 flex items-start gap-2">
                  <Megaphone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{t('auth.sponsorWarning')}</span>
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="submit-register-btn"
                className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-heading font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all disabled:opacity-50 group mt-2">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('auth.creating')}</>
                ) : (
                  <>{t('auth.registerButton')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="border-t border-white/[0.06] px-8 py-5 text-center">
              <p className="text-sm text-slate-500">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-brand font-heading font-bold uppercase text-xs tracking-wider hover:text-brand/80 transition-colors">
                  {t('auth.signIn')}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedBackground>
  );
};

export default RegisterPage;
